import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '@rydres/api-client';
import { BRAND } from '@rydres/shared';
import { ChatSheet, InfoTip, Notice, fmtPhone, useConversation } from '@rydres/ui';
import { passengerChat, riderChat, type RiderConversation, type RiderConversations } from '../lib/passenger.js';
import { Shell, useRiderGate } from './Passenger.js';

/** Asks the header to count unread messages again. */
const unreadChanged = () => window.dispatchEvent(new Event('rydres:unread'));
const ID = /^[0-9a-f-]{36}$/i;
const CHIP = 'inline-flex min-h-11 items-center rounded-xl bg-card2 px-4 text-sm font-bold disabled:opacity-60';

const when = (iso: string) => {
  const d = new Date(iso);
  return d.toDateString() === new Date().toDateString()
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};
const lastLine = (c: RiderConversation) =>
  c.closed ? 'Messages turned off' : c.last ? `${c.last.mine ? 'You: ' : ''}${c.last.body}` : 'No messages yet';

function Avatar({ name, url }: { name: string; url: string | null }) {
  return url ? (
    <img src={url} alt="" className="h-11 w-11 shrink-0 rounded-xl object-cover" />
  ) : (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand font-black text-on-brand">{name.slice(0, 1)}</div>
  );
}

function UnreadCount({ n }: { n: number }) {
  if (n <= 0) return null;
  return (
    <span className="shrink-0 rounded-full bg-danger px-2 py-0.5 text-xs font-black text-card" aria-label={`${n} unread`}>
      {n > 99 ? '99+' : n}
    </span>
  );
}

/**
 * rydres.com/me/people: the passenger's drivers, each with Message and Call. A conversation opens
 * on top of the list (`?chat=<id>`); `?with=<handle>`, from a ride's status page, opens the one
 * with that driver.
 */
export function PeoplePage() {
  const { p, gate } = useRiderGate('People', `Sign in with the mobile number you book with to message or call your ${BRAND.name} drivers.`);
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState<RiderConversations | null>(null);
  const [error, setError] = useState<string | null>(null);
  const signedIn = Boolean(p.me);
  const chat = params.get('chat');
  const open = chat && ID.test(chat) ? chat : null;
  const withHandle = params.get('with');

  const load = useCallback(() => {
    riderChat.list().then(setData, (err) => setError(err instanceof ApiError ? err.message : 'Could not load your messages.'));
  }, []);
  useEffect(() => {
    if (!signedIn) return;
    load();
    const timer = setInterval(() => document.visibilityState === 'visible' && load(), 15_000);
    window.addEventListener('rydres:unread', load);
    return () => {
      clearInterval(timer);
      window.removeEventListener('rydres:unread', load);
    };
  }, [signedIn, load]);

  const openChat = useCallback(
    (id: string, replace = false) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('with');
          next.set('chat', id);
          return next;
        },
        replace ? { replace: true } : { state: { chatOpened: true } },
      ),
    [setParams],
  );
  const close = useCallback(() => {
    if ((location.state as { chatOpened?: boolean } | null)?.chatOpened) navigate(-1);
    else
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('chat');
          return next;
        },
        { replace: true },
      );
  }, [location.state, navigate, setParams]);

  // From a ride's status page: straight into the conversation with that driver.
  useEffect(() => {
    if (!signedIn || !withHandle) return;
    riderChat.open(withHandle).then(
      (c) => openChat(c.id, true),
      (err) => setError(err instanceof ApiError ? err.message : 'Could not open the conversation.'),
    );
  }, [signedIn, withHandle, openChat]);

  if (gate) return gate;

  const drivers = p.me!.drivers;
  const byHandle = new Map((data?.conversations ?? []).map((c) => [c.handle, c]));
  const listed = new Set(drivers.map((d) => d.handle));
  const earlier = data?.conversations.filter((c) => !c.handle || !listed.has(c.handle)) ?? [];
  const message = async (handle: string, existing: RiderConversation | undefined) => {
    setError(null);
    try {
      openChat(existing?.id ?? (await riderChat.open(handle)).id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not open the conversation.');
    }
  };
  const share = async (handle: string, on: boolean) => {
    setError(null);
    try {
      await p.share(handle, on);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change that.');
    }
  };

  return (
    <>
      <Shell>
        <h1 className="flex items-center gap-2 text-2xl font-black">
          People
          <InfoTip label="sharing your details">A driver you share with sees your details as you keep them up to date. Drivers never see each other, or which other drivers you use.</InfoTip>
        </h1>
        {error && <Notice tone="danger">{error}</Notice>}
        {drivers.length === 0 ? (
          <p className="text-sm text-ink2">Drivers you book or share your details with show up here, with a button to message or call them.</p>
        ) : (
          <ul className="space-y-2">
            {drivers.map((d) => {
              const c = byHandle.get(d.handle);
              const name = d.displayName ?? 'Your driver';
              return (
                <li key={d.handle} className="space-y-3 rounded-2xl border border-line bg-card p-3">
                  <div className="flex items-center gap-3">
                    <Avatar name={name} url={d.photoUrl} />
                    <div className="min-w-0 flex-1">
                      <Link className={`block truncate underline-offset-2 hover:underline ${c?.unread ? 'font-black' : 'font-bold'}`} to={`/me/drivers/${d.handle}`}>{name}</Link>
                      <div className="truncate text-sm text-ink2">{c?.last ? `${when(c.last.at)} · ${lastLine(c)}` : `rydres.com/${d.handle}`}</div>
                    </div>
                    {c && <UnreadCount n={c.unread} />}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(d.canMessage || c) && (
                      <button type="button" onClick={() => void message(d.handle, c)} className={CHIP} aria-label={`Message ${name}`}>
                        Message
                      </button>
                    )}
                    {d.phone && (
                      <a href={`tel:${d.phone}`} className={CHIP} aria-label={`Call ${name} at ${fmtPhone(d.phone)}`}>
                        Call<span className="hidden md:inline">&nbsp;{fmtPhone(d.phone)}</span>
                      </a>
                    )}
                    <Link to={`/me/request/${d.handle}`} className={CHIP}>Add Ride Service</Link>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-ink2">
                    <input type="checkbox" checked={d.shared} onChange={(e) => void share(d.handle, e.target.checked)} />
                    Sees my details
                  </label>
                </li>
              );
            })}
          </ul>
        )}

        {earlier.length > 0 && (
          <section className="space-y-2">
            <h2 className="px-1 text-xs font-bold uppercase tracking-wide text-muted">Earlier conversations</h2>
            {earlier.map((c) => (
              <button key={c.id} type="button" onClick={() => openChat(c.id)} className="flex w-full items-center gap-3 rounded-2xl border border-dashed border-line p-3 text-left">
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className={`truncate ${c.unread ? 'font-black' : 'font-bold'}`}>{c.name}</span>
                    {c.last && <span className="shrink-0 text-xs text-muted">{when(c.last.at)}</span>}
                  </div>
                  <div className="truncate text-sm text-ink2">{lastLine(c)}</div>
                </div>
                <UnreadCount n={c.unread} />
              </button>
            ))}
          </section>
        )}

      </Shell>
      {open && <RiderChat id={open} onClose={close} />}
    </>
  );
}

function RiderChat({ id, onClose }: { id: string; onClose: () => void }) {
  const convo = useConversation(id, passengerChat, unreadChanged);
  return <ChatSheet key={id} convo={convo} onClose={onClose} />;
}
